import { Injectable } from '@angular/core';
import { MAX_PATIENT_AGE } from 'src/app/constants/max-patient-age.const';
import { CustomValidationErrors } from 'src/app/models/custom-validation-errors.model';
import { ValidationErrorNamespaceEnum } from 'src/app/enums/validation-error-namespace.enum';
import { MAX_LENGTH_FIRST_NAME, MAX_LENGTH_MIDDLE_NAME, MAX_LENGTH_LAST_NAME } from 'src/app/constants/max-length-name.const';
import { MAX_LENGTH_EMAIL } from 'src/app/constants/max-length-email.const';

@Injectable({
  providedIn: 'root'
})
export class CustomErrorMessagesService {
  public getNameMaxLength(controlName: string): number {
    switch (controlName) {
      case 'First Name':
        return MAX_LENGTH_FIRST_NAME;
      case 'Middle Name':
        return MAX_LENGTH_MIDDLE_NAME;
      case 'Last Name':
        return MAX_LENGTH_LAST_NAME;
      default:
        return 40; // fallback
    }
  }

  public getErrorMessage(errors: CustomValidationErrors | undefined | null): string {
    if (!errors) {
      return '';
    }

    if (errors.required || errors.requiredNotNull) {
      return 'This field is required.';
    }

    if (errors.invalidSpacing) {
      return `Only a single space character is allowed between strings. Leading spaces or multiple space characters between strings are not allowed.`;
    }

    if (errors.invalidName) {
      const requiredLength = errors?.maxlength?.requiredLength ?? this.getNameMaxLength(errors.invalidName);
      return `${errors.invalidName} must be ${requiredLength} characters or less and can only include the following characters: a-z, A-Z, . (period), ' (single quote), - (dash/hyphen), (spaces)`;
    }

    if (errors.invalidBirthDate) {
      return 'Enter a valid date of birth.';
    }

    if (errors.futureBirthDate) {
      return 'Enter a valid date of birth. The date of birth provided cannot be a date in the future.';
    }

    if (errors.tooOldBirthDate) {
      return `Enter a valid date of birth. The date of birth provided cannot be more than ${MAX_PATIENT_AGE} years ago.`;
    }

    if (errors.invalidZipCode) {
      if (errors.invalidZipCode === 'US') {
        return 'US zip codes must be in the following format(s): ##### or #####-####';
      } else if (errors.invalidZipCode === 'CA') {
        return 'Canadian zip codes must be in the following format: A#A #A#';
      }
    }

    if (errors.invalidPhoneNumber) {
      if (errors.invalidPhoneNumber.onlyNumeric) {
        return 'Only numerical values can be provided for phone numbers.';
      }
      if (errors.invalidPhoneNumber.exceedMaxLength) {
        return 'Phone number must be 20 characters or less.';
      }
    }

    if (errors.invalidConfirmationNumber) {
      if (errors.invalidConfirmationNumber.onlyNumeric) {
        return 'Only numerical values can be provided for confirmation numbers.';
      }
      if (errors.invalidConfirmationNumber.exceedMaxLength) {
        return 'Confirmation number must be 9 digits or less.';
      }
      if (errors.invalidConfirmationNumber.lessThanMaxLength) {
        return 'Confirmation number must be 9 digits long.';
      }
    }

    if (errors.customMaxLength) {
      if (errors.customMaxLength.validationErrorNamespace === ValidationErrorNamespaceEnum.City) {
        return `City addresses must be ${errors.customMaxLength.requiredLength} characters or less.`;
      }
      if (errors.customMaxLength.validationErrorNamespace === ValidationErrorNamespaceEnum.State) {
        return `State addresses must be ${errors.customMaxLength.requiredLength} characters or less.`;
      }
      if (errors.customMaxLength.validationErrorNamespace === ValidationErrorNamespaceEnum.Street) {
        return `Street addresses must be ${errors.customMaxLength.requiredLength} characters or less.`;
      }
    }

    if (errors.minlength) {
      return `Value must be atleast ${errors.minlength.requiredLength} characters long.`;
    }

    if (errors.invalidEmail) {
      const maxLength = errors.maxlength?.requiredLength ?? MAX_LENGTH_EMAIL;
      return `${errors.invalidEmail} must be a valid email address using the format "example@domain.com". `
              + `The email must include a local part (before "@"), a domain name (after "@"), `
              + `and a valid top-level domain (e.g., ".com", ".org"). `
              + `It must also be ${maxLength} characters or less.`;
    }

    if (errors.emailConfirmationMismatch) {
      return 'Email confirmation must match the email address provided.';
    }

    return 'Invalid field value.';
  }
}
